import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { CtaButton } from '../../../shared-components/CtaButton/CtaButton';
import { Number as NumberInput } from '../../../shared-components/Number/Number';
import { StandardOrgSettingInput } from '../../../shared-components/StandardSettingInput';

import {
  canEditSecureArchive,
  MAX_SECURE_ARCHIVE_RETENTION_DAYS,
} from '../../../store/admin/orgSettings/thunk';
import styles from '../OrganizationSettings.module.css';
import { daysToPeriod, formatRetention, periodToDays, RetentionPeriod } from './retentionPeriod';
import { actions, thunk, useAppDispatch, useAppSelector } from 'redux-stores';
import TCClient from 'client';

// view_archiving is only offered to orgs that have the message archive rolled out. /roles hides
// archiving roles from callers who can't grant them (DE-4161), so this only works for staff and
// archiving admins.
const ARCHIVE_ROLLOUT_ROLE = 'view_archiving';

const EMPTY_PERIOD: RetentionPeriod = { years: '', months: '', days: '' };
// Per-field caps keep the arithmetic exact; save still checks the 100-year total
const PERIOD_FIELD_MAX: Record<keyof RetentionPeriod, number> = {
  years: 100,
  months: 1200,
  days: MAX_SECURE_ARCHIVE_RETENTION_DAYS,
};
const PERIOD_FIELDS: Array<{ field: keyof RetentionPeriod; label: string }> = [
  { field: 'years', label: 'years' },
  { field: 'months', label: 'months' },
  { field: 'days', label: 'days' },
];

export default function MessageArchiveRetention() {
  const dispatch = useAppDispatch();
  const [isRolledOut, setIsRolledOut] = useState(false);
  const { canEdit, isPurgeEnabled, organizationId, retentionDays } = useAppSelector(
    (state) => ({
      canEdit: canEditSecureArchive(state.admin.permissions),
      isPurgeEnabled: state.orgSettings.settings.isSecureArchivePurgeEnabled,
      organizationId: state.admin.selectedOrganizationId,
      retentionDays: state.orgSettings.settings.secureArchiveRetentionDays,
    }),
    shallowEqual
  );

  useEffect(() => {
    // The lookup can't see view_archiving for other admins, so only editors run it
    if (!canEdit) return;
    let isMounted = true;
    TCClient.adminOrganizations
      .getAllPermissionRoles({ orgId: organizationId })
      .then((response: unknown) => {
        const { roles = [] } = (response ?? {}) as { roles?: Array<string | { name: string }> };
        const names = roles.map((role) => (typeof role === 'string' ? role : role.name));
        if (isMounted) setIsRolledOut(names.includes(ARCHIVE_ROLLOUT_ROLE));
      })
      .catch((error) => {
        Sentry.logger.warn('Error loading roles for message archive retention', { error });
        if (isMounted) setIsRolledOut(false);
      });
    return () => {
      isMounted = false;
    };
  }, [canEdit, organizationId]);

  // The three fields are local; only the combined total in days is stored. Resync them from the
  // store only when the stored value changes from outside (fetch, cancel), not from our own edits.
  const [period, setPeriod] = useState<RetentionPeriod>(EMPTY_PERIOD);
  const lastDispatchedDays = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (Object.is(retentionDays, lastDispatchedDays.current)) return;
    setPeriod(
      retentionDays === undefined || !Number.isInteger(retentionDays)
        ? EMPTY_PERIOD
        : daysToPeriod(retentionDays)
    );
  }, [retentionDays]);

  const onPeriodChange = (field: keyof RetentionPeriod, value: string) => {
    const nextPeriod = { ...period, [field]: value };
    const totalDays = periodToDays(nextPeriod);
    setPeriod(nextPeriod);
    lastDispatchedDays.current = totalDays;
    dispatch(actions.updateSettings({ secureArchiveRetentionDays: totalDays }));
  };

  // Retention and purge rules are checked inside saveSettings, so every Save button applies them
  const onSave = useCallback(() => thunk.saveSettings({ dispatch }), [dispatch]);

  // Other admins can't see view_archiving in /roles, so show them the section once an editor
  // has configured a value; until then there is nothing to show read-only
  const isConfigured = isPurgeEnabled !== undefined || !!retentionDays;
  const isVisible = canEdit ? isRolledOut : isRolledOut || isConfigured;
  if (!isVisible) return null;

  return (
    <div data-test-id="message-archive-retention">
      <div className={styles.flexListStyleWithPadding}>
        <div className={styles.labelStyle}>
          <div className={styles.mainLabelStyle}>Message Archive Retention</div>
          <div className={styles.sideLabelStyle}>
            {canEdit
              ? 'Archived messages older than the retention period are deleted when purge is enabled.'
              : 'Only archiving admins can change these settings.'}
          </div>
        </div>
      </div>
      <div className={styles.flexListStyleWithPadding}>
        <div className={styles.mainLabelStyle}>Purge</div>
        {canEdit ? (
          <StandardOrgSettingInput
            type="radio"
            settingName="isSecureArchivePurgeEnabled"
            options={[
              { label: 'Off', value: false },
              { label: 'On', value: true },
            ]}
          />
        ) : (
          <div>{isPurgeEnabled ? 'On' : 'Off'}</div>
        )}
      </div>
      <div className={styles.flexListStyleWithPadding}>
        <div className={styles.labelStyle}>
          <div className={styles.mainLabelStyle}>Retention</div>
          {canEdit && (
            <div className={styles.sideLabelStyle}>
              {isPurgeEnabled ? 'Saved as days.' : 'Purge is off, so nothing is deleted.'}
              {Number.isInteger(retentionDays) && ` Total: ${retentionDays} days`}
            </div>
          )}
        </div>
        {canEdit ? (
          <div className={styles.retentionPeriodInputs}>
            {PERIOD_FIELDS.map(({ field, label }) => (
              <label key={field} className={styles.retentionPeriodField}>
                <NumberInput
                  dataTestId={`secureArchiveRetention ${field}`}
                  disabled={!isPurgeEnabled}
                  min={0}
                  max={PERIOD_FIELD_MAX[field]}
                  value={period[field]}
                  onInputChange={(val: string) => onPeriodChange(field, val)}
                />
                {label}
              </label>
            ))}
          </div>
        ) : (
          <div>{retentionDays ? formatRetention(retentionDays) : 'Not set'}</div>
        )}
      </div>
      {canEdit && (
        <div>
          <CtaButton primary label="Save" onClick={onSave} />
        </div>
      )}
    </div>
  );
}
